import { CashDroPaymentPhase } from "@/shared/atoms/useCashDroPaymentAtom";
import useCashDroPaymentHook from "@/shared/hooks/useCashDroPaymentHook";
import { CashDroOperationType } from "@/shared/utils/cashDro";
import { formatPrice } from "@/shared/utils/helpers";
import { useTranslation } from "react-i18next";
import { Prompt } from "react-router-dom";
import Button from "../Button/Button";
import Modal, { ModalProps } from "../Modal/Modal";
import ProgressBar from "../Progress/Progress";
import Spinner from "../Spinner/Spinner";

// While in these phases money may be moving, so the modal cannot be closed.
const BUSY_PHASES: CashDroPaymentPhase[] = [
  "starting",
  "collecting",
  "cancelling",
  "saving",
];

// Each phase belongs to one of the three steps: connect, move the money, save.
const PHASE_STEPS: Record<CashDroPaymentPhase, number> = {
  starting: 0,
  collecting: 1,
  cancelling: 1,
  partial: 1,
  saving: 2,
  saveFailed: 2,
  failed: 0,
};

const ERROR_PHASES: CashDroPaymentPhase[] = ["saveFailed", "partial", "failed"];

const CashDroPaymentModal = (props: ModalProps) => {
  const { t } = useTranslation();
  const { state, cancel, retrySave, close } = useCashDroPaymentHook();

  if (!state) {
    return null;
  }

  const { type, phase, amount, operationId, totalIn, totalOut, error } = state;
  const isRefund = type === CashDroOperationType.PAYMENT;
  const isBusy = BUSY_PHASES.includes(phase);
  const received = formatPrice(totalIn / 100);
  const paidOut = formatPrice(totalOut / 100);

  const renderBody = () => {
    switch (phase) {
      case "starting":
        return <Pending text={t("cashDro.connecting")} />;
      case "cancelling":
        return (
          <Pending
            text={t(
              isRefund ? "cashDro.cancellingRefund" : "cashDro.cancelling"
            )}
          />
        );
      case "saving":
        return (
          <Pending
            text={t(isRefund ? "cashDro.savingRefund" : "cashDro.saving")}
          />
        );
      case "collecting":
        return (
          <>
            <h5>{t(isRefund ? "cashDro.takeRefund" : "cashDro.insertCash")}</h5>
            {isRefund ? (
              <Row label={t("cashDro.paidOut")} value={paidOut} />
            ) : (
              <>
                <Row label={t("cashDro.received")} value={received} />
                <Row label={t("cashDro.changeGiven")} value={paidOut} />
              </>
            )}
            <Button
              variant="danger"
              className="w-full mt-4"
              isDisabled={!operationId}
              onClick={cancel}
            >
              {t(isRefund ? "cashDro.cancelRefund" : "cashDro.cancelPayment")}
            </Button>
          </>
        );
      case "saveFailed":
        return (
          <>
            <p className="text-error">
              {isRefund
                ? t("cashDro.refundSaveFailed", {
                    amount: paidOut,
                    operationId,
                  })
                : t("cashDro.saveFailed", { amount: received, operationId })}
            </p>
            <div className="w-full flex gap-4 mt-4">
              <Button variant="secondary" className="w-full" onClick={close}>
                {t("close")}
              </Button>
              <Button className="w-full" onClick={retrySave}>
                {t("cashDro.retrySave")}
              </Button>
            </div>
          </>
        );
      case "partial":
        return (
          <>
            <p className="text-error">
              {t("cashDro.partial", {
                received,
                amount: formatPrice(amount),
                operationId,
              })}
            </p>
            <Button variant="secondary" className="w-full mt-4" onClick={close}>
              {t("close")}
            </Button>
          </>
        );
      case "failed":
        return (
          <>
            <p className="text-error">{error}</p>
            {operationId && (
              <p>{t("cashDro.checkTerminal", { operationId })}</p>
            )}
            <Button variant="secondary" className="w-full mt-4" onClick={close}>
              {t("close")}
            </Button>
          </>
        );
    }
  };

  return (
    <Modal width="40rem" {...props} onExit={isBusy ? undefined : close}>
      <Prompt when={isBusy} message={t("cashDro.doNotLeave")} />
      <div className="flex flex-col items-center gap-4 pt-8 pb-4 text-center">
        <h3>{t(isRefund ? "cashDro.refundTitle" : "cashDro.title")}</h3>
        <div className="text-3xl font-semibold">{formatPrice(amount)}</div>
        <CashDroProgress
          phase={phase}
          step={phase === "failed" && operationId ? 1 : PHASE_STEPS[phase]}
          moved={(isRefund ? totalOut : totalIn) / 100 / amount}
          labels={[
            t("cashDro.steps.connect"),
            t(isRefund ? "cashDro.steps.refund" : "cashDro.steps.payment"),
            t("cashDro.steps.save"),
          ]}
        />
        {renderBody()}
      </div>
    </Modal>
  );
};

const CashDroProgress = ({
  phase,
  step,
  moved,
  labels,
}: {
  phase: CashDroPaymentPhase;
  step: number;
  moved: number;
  labels: string[];
}) => {
  const isError = ERROR_PHASES.includes(phase);
  const stepProgress = step === 1 ? Math.min(Math.max(moved || 0, 0), 1) : 0.5;
  const value = ((step + stepProgress) / labels.length) * 100;

  return (
    <div className="w-full flex flex-col gap-2">
      <ProgressBar value={value} color={isError ? "var(--error)" : undefined} />
      <div className="grid grid-cols-3 text-small">
        {labels.map((label, index) => (
          <span
            key={label}
            className={
              index === step
                ? `font-bold ${isError ? "text-error" : "text-primary"}`
                : index < step
                  ? "text-text"
                  : "text-text-tertiary"
            }
          >
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};

const Pending = ({ text }: { text: string }) => (
  <>
    <Spinner size="0.8rem" />
    <h5>{text}</h5>
  </>
);

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="w-full flex justify-between">
    <span>{label}</span>
    <span className="font-semibold">{value}</span>
  </div>
);

export default CashDroPaymentModal;
